import { useEffect, useRef, useState } from 'react';

import { FeatureFlagKeys, experiment } from '@lib/experiment';

const subscribe = (listener: () => void) => experiment.subscribe(listener);

const getSnapshot = (flag: FeatureFlagKeys) => experiment.isFeatureEnabled(flag);

export const useFeatureFlagObserver = (
  flag: FeatureFlagKeys,
  handlers: {
    onChange?: (value: boolean) => void;
    onEnable?: () => void;
    onDisable?: () => void;
  } = {},
) => {
  const [value, setValue] = useState(() => getSnapshot(flag));

  // The subscription outlives renders, so it compares against the last value it saw, not the one
  // captured when it subscribed, and calls the caller's latest handlers.
  const valueRef = useRef(value);
  const handlersRef = useRef(handlers);

  useEffect(() => {
    handlersRef.current = handlers;
  });

  useEffect(() => {
    const handleChange = () => {
      const next = getSnapshot(flag);

      if (next === valueRef.current) {
        return;
      }

      valueRef.current = next;
      const { onChange, onEnable, onDisable } = handlersRef.current;

      if (next) {
        onEnable?.();
      } else {
        onDisable?.();
      }

      setValue(next);
      onChange?.(next);
    };

    handleChange();

    return subscribe(handleChange);
  }, [flag]);

  return value;
};
